import { useTranslation } from 'react-i18next';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_PATTERN = /^\d{6}$/;

/**
 * 新規パスワードの規則。Cognito pool のポリシー（8 文字以上・数字を含む）+ Cognito の Password は [\S]+ なので
 * 先頭末尾の空白は不可（API 側の NEW_PASSWORD_RULES と同じ）。ログインの入力には掛けない
 */
export const isValidPassword = (value: string): boolean =>
  value.length >= 8 && /\d/.test(value) && value === value.trim();

/**
 * 自前ログイン画面の入力チェック規則と、API の失敗 code → 文言。
 * useAccount の validationRules と同じ流儀（react-hook-form の rules）。
 */
export default function useAuth() {
  const { t, i18n } = useTranslation();

  const validationRules = {
    email: {
      required: t('pages:auth:messages:email_required'),
      pattern: { value: EMAIL_PATTERN, message: t('pages:auth:messages:email_format') },
      maxLength: { value: 128, message: t('pages:auth:messages:email_max_length') },
    },
    password: {
      required: t('pages:auth:messages:password_required'),
      // 戻り値を明示的に boolean | string へ固定する。t() の戻り型 DefaultTFuncReturn（string | null）を
      // そのまま返すと react-hook-form の ValidateResult（string | boolean | undefined）と null で衝突するため
      // （既存キーは必ず文字列を返すので String() での挙動変化は無い）
      validate: (value: string): boolean | string =>
        isValidPassword(value) || String(t('pages:auth:messages:password_rule')),
    },
    passwordConfirm: (password: string) => ({
      required: t('pages:auth:messages:password_required'),
      validate: (value: string): boolean | string =>
        value === password || String(t('pages:auth:messages:password_confirm_mismatch')),
    }),
    code: {
      required: t('pages:auth:messages:code_required'),
      pattern: { value: CODE_PATTERN, message: t('pages:auth:messages:code_format') },
    },
  };

  /** API の失敗応答 code → 利用者向け文言。未知の code は汎用文言（spec §3.7） */
  const errorMessage = (code?: string): string => {
    const key = `pages:auth:errors:${code ?? 'unknown'}`;
    return i18n.exists(key) ? t(key) : t('pages:auth:errors:unknown');
  };

  return { validationRules, errorMessage };
}
